/**
 * JoinLocalGame - Page for joining a local game slot
 * 
 * Two entry points:
 * 1. QR scan - arrives with ?token=xxx
 * 2. Manual code entry - user types 4-digit code
 * 
 * Flow:
 * - If logged in: Show one-tap confirm
 * - If not logged in: Show login form, then confirm
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Users, LogIn } from 'lucide-react';
import { playClick, playSuccess } from '@/utils/sounds';
import AvatarCanvas from '@/components/AvatarCanvas';
import RadioTower from '@/components/RadioTower';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinLocalGame() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, getAuthHeader } = useAuth();
  
  const [step, setStep] = useState('loading'); // loading, enter-code, login, confirm, success, error
  const [code, setCode] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Check for token in URL on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      fetchSessionByToken(token);
    } else {
      setStep('enter-code');
    }
  }, [searchParams]);

  const fetchSessionByToken = async (token) => {
    try {
      const response = await axios.get(`${API}/local-link/info-by-token?token=${token}`);
      setSessionInfo(response.data);
      setCode(response.data.code);
      
      if (response.data.expired) {
        setStep('error');
        setError('This link has expired. Ask the host for a new code.');
      } else if (response.data.claimed) {
        setStep('error');
        setError('This slot has already been claimed.');
      } else if (user) {
        setStep('confirm');
      } else {
        setStep('login');
      }
    } catch (err) {
      setStep('error');
      setError('Invalid or expired link. Ask the host for a new code.');
    }
  };

  const fetchSessionByCode = async () => {
    if (code.length !== 4) {
      toast.error('Please enter a 4-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/local-link/info/${code}`);
      setSessionInfo(response.data);
      
      if (response.data.expired) {
        setStep('error');
        setError('This code has expired. Ask the host for a new code.');
      } else if (response.data.claimed) {
        setStep('error');
        setError('This slot has already been claimed.');
      } else if (user) {
        setStep('confirm');
      } else {
        setStep('login');
      }
    } catch (err) {
      toast.error('Invalid code. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    playClick();
    setLoading(true);
    
    try {
      await login(email, password);
      playSuccess();
      setStep('confirm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    playClick();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/local-link/claim/${code}`,
        {},
        { headers: getAuthHeader() }
      );
      
      playSuccess();
      setStep('success');
      toast.success('Successfully joined the game!');
    } catch (err) {
      setStep('error');
      setError(err.response?.data?.detail || 'Failed to join. The code may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    playClick();
    setCode('');
    setSessionInfo(null);
    setError(null);
    setStep('enter-code');
  };

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <RadioTower size={60} animated={false} className="mx-auto mb-4" />
          <h1 className="retro-heading text-xl text-ink">JOIN LOCAL GAME</h1>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div className="pixel-card text-center py-8">
            <div className="animate-pulse">
              <Users className="mx-auto text-grass" size={48} />
            </div>
            <p className="retro-text text-sm text-dirt mt-4">Loading...</p>
          </div>
        )}

        {/* Enter Code */}
        {step === 'enter-code' && (
          <div className="pixel-card">
            <h2 className="retro-heading text-sm text-ink mb-4 text-center">
              ENTER JOIN CODE
            </h2>
            
            <p className="retro-text text-xs text-dirt text-center mb-6">
              Enter the 4-digit code shown on the host screen
            </p>
            
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="pixel-input w-full text-center text-2xl tracking-widest mb-6"
              placeholder="0000"
              maxLength={4}
              inputMode="numeric"
              autoFocus
              data-testid="join-code-input"
            />
            
            <button
              onClick={fetchSessionByCode}
              disabled={loading || code.length !== 4}
              className="pixel-btn pixel-btn-primary w-full"
              data-testid="submit-code-btn"
            >
              {loading ? 'CHECKING...' : 'JOIN'}
            </button>
          </div>
        )}

        {/* Login */}
        {step === 'login' && sessionInfo && (
          <div className="pixel-card">
            <h2 className="retro-heading text-sm text-ink mb-4 text-center">
              LOG IN TO JOIN
            </h2>
            
            <div className="bg-grass/10 p-3 rounded-lg border-2 border-grass mb-6 text-center">
              <p className="retro-text text-xs text-dirt">Joining</p>
              <p className="retro-heading text-sm text-grass">
                {sessionInfo.team_name || `Team ${sessionInfo.team}`}
              </p>
              <p className="retro-text text-xs text-dirt">
                Player {sessionInfo.slot_index + 1}
                {sessionInfo.host_username && ` • Hosted by ${sessionInfo.host_username}`}
              </p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="retro-text text-xs text-ink block mb-1">EMAIL</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pixel-input w-full"
                  required
                  autoFocus
                  data-testid="join-email-input"
                />
              </div>
              
              <div>
                <label className="retro-text text-xs text-ink block mb-1">PASSWORD</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pixel-input w-full"
                  required
                  data-testid="join-password-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="pixel-btn pixel-btn-primary w-full"
                data-testid="join-login-btn"
              >
                <LogIn size={16} className="inline mr-2" />
                {loading ? 'LOGGING IN...' : 'LOG IN & JOIN'}
              </button>
            </form>
            
            <button
              onClick={handleGoBack}
              className="w-full mt-4 retro-text text-xs text-dirt hover:text-ink underline text-center"
            >
              Back to code entry
            </button>
          </div>
        )}

        {/* Confirm (logged in) */}
        {step === 'confirm' && sessionInfo && user && (
          <div className="pixel-card text-center">
            <h2 className="retro-heading text-sm text-ink mb-4">
              CONFIRM JOIN
            </h2>
            
            {/* User info */}
            <div className="flex justify-center mb-4">
              {user.avatar_config ? (
                <AvatarCanvas config={user.avatar_config} size={80} />
              ) : (
                <div className="w-20 h-20 rounded-full bg-grass flex items-center justify-center">
                  <Users className="text-cream" size={32} />
                </div>
              )}
            </div>
            
            <p className="retro-heading text-lg text-grass mb-2">{user.username}</p>
            
            <div className="bg-grass/10 p-4 rounded-lg border-2 border-grass mb-6">
              <p className="retro-text text-xs text-dirt">Joining as Player {sessionInfo.slot_index + 1}</p>
              <p className="retro-heading text-sm text-grass mt-1">
                {sessionInfo.team_name || `Team ${sessionInfo.team}`}
              </p>
              {sessionInfo.host_username && (
                <p className="retro-text text-xs text-dirt mt-1">
                  Hosted by {sessionInfo.host_username}
                </p>
              )}
            </div>
            
            <button
              onClick={handleClaim}
              disabled={loading}
              className="pixel-btn pixel-btn-primary w-full"
              data-testid="confirm-join-btn"
            >
              <Check size={16} className="inline mr-2" />
              {loading ? 'JOINING...' : 'JOIN GAME'}
            </button>
            
            <button
              onClick={handleGoBack}
              className="w-full mt-4 retro-text text-xs text-dirt hover:text-ink underline"
            >
              Use a different code
            </button>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="pixel-card text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-grass flex items-center justify-center">
                <Check className="text-cream" size={40} />
              </div>
            </div>
            
            <h2 className="retro-heading text-lg text-grass mb-2">YOU&apos;RE IN!</h2>
            
            <p className="retro-text text-sm text-dirt mb-6">
              Your account is now linked. The host screen will update automatically.
              You can close this page.
            </p>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="pixel-btn w-full"
              data-testid="go-dashboard-btn"
            >
              GO TO DASHBOARD
            </button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="pixel-card text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-4xl">😕</span>
              </div>
            </div>
            
            <h2 className="retro-heading text-lg text-red-600 mb-2">OOPS!</h2>
            
            <p className="retro-text text-sm text-dirt mb-6">{error}</p>
            
            <button
              onClick={handleGoBack}
              className="pixel-btn pixel-btn-primary w-full"
              data-testid="try-again-btn"
            >
              TRY ANOTHER CODE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
